import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, 
  Plus, 
  Download,
  Search,
  Loader2,
  Trash2,
  Upload
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function CasesPage() {
  const { isSuperAdmin, organizationName } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [casesRes] = await Promise.all([
        casesAPI.getAll()
      ]);
      setCases(casesRes.data);
      
      if (isSuperAdmin) {
        const orgsRes = await organizationsAPI.getAll();
        setOrganizations(orgsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCases();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, statusFilter, orgFilter]);

  const fetchCases = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (orgFilter && orgFilter !== 'all') params.organization_id = orgFilter;
      
      const response = await casesAPI.getAll(params);
      setCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases');
    }
  };

  const handleExport = async () => {
    try {
      const response = await casesAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cases_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await casesAPI.delete(deleteId);
      setCases(cases.filter(c => c.id !== deleteId));
      toast.success('Case deleted successfully');
    } catch (error) {
      toast.error('Failed to delete case');
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-success text-white',
      'Pending': 'bg-warning text-white',
      'Approved': 'bg-primary text-white',
      'Denied': 'bg-error text-white',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              Cases
            </h1>
            <p className="font-body text-muted-foreground">
              {isSuperAdmin ? 'Manage all immigration cases' : `Immigration cases for ${organizationName}`}
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="font-body"
              onClick={handleExport}
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Link to="/cases/import">
              <Button variant="outline" className="font-body" data-testid="bulk-import-btn">
                <Upload className="w-4 h-4 mr-2" />
                Bulk Import
              </Button>
            </Link>
            <Link to="/cases/new">
              <Button className="btn-lift bg-primary text-white font-body" data-testid="add-case-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Case
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, A#, or USCIS receipt..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 font-body bg-card"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 font-body bg-card" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Denied">Denied</SelectItem>
            </SelectContent>
          </Select>
          {isSuperAdmin && organizations.length > 0 && (
            <Select value={orgFilter} onValueChange={setOrgFilter}>
              <SelectTrigger className="w-48 font-body bg-card" data-testid="org-filter">
                <SelectValue placeholder="All Organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-body text-muted-foreground mb-4">No cases found</p>
              <Link to="/cases/new">
                <Button className="btn-lift bg-primary text-white font-body" data-testid="add-first-case-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Case
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-semibold">Name</TableHead>
                  {isSuperAdmin && <TableHead className="font-heading font-semibold">Organization</TableHead>}
                  <TableHead className="font-heading font-semibold">Country</TableHead>
                  <TableHead className="font-heading font-semibold">Service Type</TableHead>
                  <TableHead className="font-heading font-semibold">A#</TableHead>
                  <TableHead className="font-heading font-semibold">Status</TableHead>
                  <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cases.map((caseItem) => (
                  <TableRow key={caseItem.id} className="hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/cases/${caseItem.id}`)} data-testid={`case-row-${caseItem.id}`}>
                    <TableCell className="font-body font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-heading text-sm font-semibold text-primary">
                            {caseItem.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        {caseItem.name}
                      </div>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className="font-body text-muted-foreground">
                        {caseItem.organization_name || '-'}
                      </TableCell>
                    )}
                    <TableCell className="font-body text-muted-foreground">
                      {caseItem.country || '-'}
                    </TableCell>
                    <TableCell className="font-body text-muted-foreground">
                      {caseItem.type_of_services || '-'}
                    </TableCell>
                    <TableCell className="font-body text-muted-foreground">
                      {caseItem.a_number || '-'}
                    </TableCell>
                    <TableCell>
                      <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${getStatusColor(caseItem.status)}`}>
                        {caseItem.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-error hover:text-error"
                          onClick={(e) => { e.stopPropagation(); setDeleteId(caseItem.id); }}
                          data-testid={`delete-case-${caseItem.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Case</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete this case? This action cannot be undone and will also delete all associated documents and status updates.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-error text-white hover:bg-error/90 font-body"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
